import { StreamLanguage } from "@codemirror/language"
import { shell } from "@codemirror/legacy-modes/mode/shell"
import { styled } from "@linaria/react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { CodeMirror } from "@/components/CodeMirror/CodeMirror.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { inputBaseStyles, inputFocusStyles } from "@/styles/inputMixins.ts"

export type ScriptEditDialogContentProps = {
  initialValue: string
  onSave: (value: string) => void
  onSaveAndRun: (value: string) => void
  isReadonly: boolean
  canRun: boolean
}

export const ScriptEditDialogContent = (
  {
    initialValue,
    onSave,
    onSaveAndRun,
    isReadonly,
    canRun
  }: ScriptEditDialogContentProps
) => {
  const initialRef = useRef(initialValue)
  const [temp, setTemp] = useState(initialValue)

  // Snapshot initial value on dialog open (mount)
  useEffect(() => {
    initialRef.current = initialValue
    setTemp(initialValue)
  }, [initialValue])

  const isDirty = useMemo(() => temp !== initialRef.current, [temp])

  return (
    <Vertical alignItems="stretch" gap="8px" width="100%">
      <ScriptContainer>
        <CodeMirror
          value={temp}
          onChange={setTemp}
          basicSetup={{ foldGutter: false, highlightActiveLine: !isReadonly, highlightActiveLineGutter: !isReadonly }}
          extensions={[StreamLanguage.define(shell)]}
          autoFocus={!isReadonly}
          readOnly={isReadonly}
        />
      </ScriptContainer>

      {!isReadonly && isDirty && (
        <ActionsRow>
          <DialogPrimitive.Close asChild>
            <Button kind="alternative" onClick={(e) => {
              e.stopPropagation()
              setTemp(initialRef.current)
            }}>Cancel</Button>
          </DialogPrimitive.Close>

          <DialogPrimitive.Close asChild>
            <Button kind="primary" onClick={(e) => {
              e.stopPropagation()
              onSave(temp)
            }}>Save</Button>
          </DialogPrimitive.Close>

          <DialogPrimitive.Close asChild>
            <Button kind="primary" disabled={!canRun} onClick={(e) => {
              e.stopPropagation()
              onSaveAndRun(temp)
            }}>Save and run</Button>
          </DialogPrimitive.Close>
        </ActionsRow>
      )}
    </Vertical>
  )
}

const ScriptContainer = styled.div`
  position: relative;
  overflow: hidden;

  width: 100%;
  max-width: 100%;
  min-width: 0;
  height: 400px;

  ${inputBaseStyles};

  &:has(.cm-editor.cm-focused) {
    ${inputFocusStyles};
  }

  .cm-editor {
    width: 100%;
    height: 400px;
  }

  .cm-scroller {
    overflow-x: auto;
  }
`

const ActionsRow = styled(Horizontal)`
  align-items: center;
  justify-content: flex-end;
  gap: 8px;

  width: 100%;
  margin-top: 8px;
`
